import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { InputHTMLAttributes, ReactElement, ReactNode } from 'react';
import VoxelIconButton from '../Buttons/VoxelIconButton';
import VoxelText from '../Text/VoxelText';
import VoxelFormsLabel from './VoxelFormsLabel';

export interface VoxelInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  labelPosition?: 'top' | 'left';
  wrapperClassNames?: string;
  inputClassNames?: string;
  outlined?: boolean;
  preText?: ReactNode;
  helperText?: string;
  showHelperText?: boolean;
  helperNotError?: boolean;
  deleteValue?: () => void;
  size?: 'sm' | 'md' | 'xxl';
  inputWidthClass?: string;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
}

const VoxelInput = React.forwardRef<HTMLInputElement, VoxelInputProps>(
  (
    {
      id,
      label,
      labelPosition = 'left',
      value,
      onChange,
      onClick,
      placeholder,
      autoFocus,
      disabled,
      maxLength,
      helperText,
      showHelperText,
      helperNotError,
      deleteValue,
      preText,
      onBlur,
      outlined = true,
      size = 'md',
      inputWidthClass: width = 'w-full',
      startIcon,
      endIcon,
      type,
      wrapperClassNames,
      readOnly,
      inputClassNames,
      ...inputProps
    },
    ref
  ) => {
    let labelPositionCSS = ` ${wrapperClassNames} `;
    if (labelPosition === 'left') {
      labelPositionCSS += ' flex-row flex-nowrap whitespace-nowrap items-center gap-x-2 ';
    }
    if (labelPosition === 'top') {
      labelPositionCSS += ' flex-col gap-y-2 ';
    }

    return (
      <div className={`flex ${labelPositionCSS} relative`}>
        <VoxelFormsLabel label={label} id={id} disabled={disabled} />
        <div className="relative group flex flex-row items-center flex-nowrap w-full">
          {preText !== null && <div className="font-medium text-voxel-blue-500">{preText}</div>}
          <input
            ref={ref}
            {...inputProps}
            onBlur={onBlur}
            id={id}
            value={value}
            onChange={onChange}
            onClick={onClick}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoComplete="off"
            spellCheck="false"
            data-1p-ignore
            autoCapitalize="off"
            autoCorrect="off"
            disabled={disabled}
            maxLength={maxLength}
            type={type}
            className={`
            rounded-md bg-white
            ${inputClassNames}
            ${width} 
            ${size === 'sm' ? 'py-1.5 px-2.5' : ''}
            ${size === 'md' ? 'py-2 px-2.5' : ''}
            ${size === 'xxl' ? 'py-2.5 pl-4 text-[16px] font-semibold font-sans' : ''}
            ${startIcon ? 'pl-10' : ''} ${endIcon ? 'pr-7' : ''}
            outline-none 
            ${showHelperText && !helperNotError && 'ring-red-500 ring-2'}
            ${!disabled && 'placeholder-voxel-gray-400'}
            ${disabled && 'placeholder-voxel-gray-300'}
            ${!disabled &&
              outlined &&
              !(showHelperText && !helperNotError) &&
              'ring-1 ring-voxel-gray-300'
              }
              ${!disabled && !(showHelperText && !helperNotError) && 'hover:ring-voxel-gray-400 '}
              ${!disabled &&
              !(showHelperText && !helperNotError) &&
              'focus:ring-2 focus:ring-primary-600 '
              }
            ${disabled && 'ring-1 ring-voxel-gray-200 text-gray-500'}
        `}
            readOnly={readOnly}
          />
          {startIcon && (
            <div className={`absolute ${size === 'md' ? 'left-3 top-1.5' : 'left-2 top-[5px]'}`}>
              {startIcon}
            </div>
          )}
          {endIcon && (
            <div className={`absolute right-2 ${size === 'md' ? 'top-1.5' : 'top-[5px]'}`}>
              {endIcon}
            </div>
          )}
          {!disabled && deleteValue && !!value && (
            <div
              className={`
                absolute z-50
                invisible group-hover:visible
                ${size === 'sm' ? 'right-1.5 top-[4px]' : ''}
                ${size === 'md' ? 'right-2 top-1.5 ' : ''} 
                `}
            >
              <VoxelIconButton
                type="reset"
                onClick={deleteValue}
                color="white"
                size="xs"
                icon={<FontAwesomeIcon icon={faXmark} />}
              />
            </div>
          )}
        </div>
        {showHelperText ? (
          <p
            className={`absolute -bottom-6 left-0.5 text-xs ${helperNotError ? 'text-voxel-gray-700' : 'text-red-500'
              }`}
          >
            {helperText}
          </p>
        ) : undefined}
      </div>
    );
  }
);

export default VoxelInput;
